import { httpCoreService } from "../services/HttpCoreService";
import { getStoredUser } from "../utils/auth";
import type {
  Site,
  CreateSiteRequest,
  UpdateSiteRequest,
  SiteFilters,
  SiteListResponse,
} from "../pages/SiteManagement/types/siteManagement.types";
import type { ApiResponse } from "../types/auth.type";

interface RawUserInfo {
  id?: string;
  name?: string;
  email?: string;
  studyName?: string;
}

interface RawStudyAssignment {
  studyName?: string;
  principalInvestigator?: string;
  coordinators?: string[];
  assignedPis?: {
    principalInvestigator?: { name?: string };
  }[];
  [key: string]: unknown;
}

interface RawSiteData {
  id?: string;
  siteNumber?: string;
  code?: string;
  site_no?: string;
  studyAssignments?: RawStudyAssignment[];
  principalInvestigators?: RawUserInfo[];
  studyCoordinators?: RawUserInfo[];
  assignedStudies?: string[];
  [key: string]: unknown;
}

interface PaginatedSitesPayload {
  items: unknown[];
  total: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

function isPaginatedSitesPayload(
  payload: unknown,
): payload is PaginatedSitesPayload {
  return (
    typeof payload === "object" &&
    payload !== null &&
    Array.isArray((payload as PaginatedSitesPayload).items) &&
    typeof (payload as PaginatedSitesPayload).total === "number"
  );
}

export interface ISiteRepository {
  createSite(site: CreateSiteRequest): Promise<Site>;
  getSites(
    filters?: SiteFilters,
    page?: number,
    limit?: number,
  ): Promise<SiteListResponse>;
  getAllSites(filters?: SiteFilters): Promise<Site[]>;
  getSitesByStudy(studyId: string): Promise<Site[]>;
  getSiteById(id: string): Promise<Site>;
  updateSite(id: string, updates: UpdateSiteRequest): Promise<Site>;
  deleteSite(id: string): Promise<void>;
  activateSite(id: string): Promise<Site>;
  archiveSite(id: string): Promise<Site>;
}

export class SiteRepository implements ISiteRepository {
  private enrichSite(data: Site | RawSiteData): Site {
    const rawData = data as RawSiteData;
    const assignments = rawData.studyAssignments ?? [];
    const enrichedAssignments = assignments.map((assignment) => {
      const assignedPiNames = (assignment.assignedPis ?? [])
        .map((ssaPi) => ssaPi.principalInvestigator?.name)
        .filter((name): name is string =>
          typeof name === "string" ? name.trim().length > 0 : false,
        );

      const matchingPI = (rawData.principalInvestigators ?? []).find(
        (pi) => pi.studyName === assignment.studyName,
      );
      const matchingCoordinators = (rawData.studyCoordinators ?? [])
        .filter((sc) => sc.studyName === assignment.studyName)
        .map((sc) => sc.name ?? "");

      const piFromAssignment =
        typeof assignment.principalInvestigator === "string"
          ? assignment.principalInvestigator.trim()
          : "";
      const piFromAssigned =
        assignedPiNames.length > 0 ? assignedPiNames.join(", ") : "";
      const piFromMatching = matchingPI?.name?.trim() ?? "";

      return {
        ...assignment,
        // Empty strings must fall through (do not use ?? here).
        principalInvestigator:
          piFromAssignment || piFromAssigned || piFromMatching || "",
        coordinators:
          Array.isArray(assignment.coordinators) &&
          assignment.coordinators.length > 0
            ? assignment.coordinators
            : matchingCoordinators,
      };
    });

    return {
      ...data,
      siteNumber: rawData.siteNumber ?? rawData.code ?? rawData.site_no,
      studyAssignments: enrichedAssignments,
      principalInvestigators: rawData.principalInvestigators ?? [],
      studyCoordinators: rawData.studyCoordinators ?? [],
      assignedStudies: rawData.assignedStudies ?? [],
    } as Site;
  }

  async createSite(site: CreateSiteRequest): Promise<Site> {
    const response: ApiResponse<Site> = await httpCoreService.post<
      Site,
      CreateSiteRequest
    >("/sites", site);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to create site",
      );
    }

    if (!response.data) {
      throw new Error(
        "Site creation succeeded but no data was returned from the server.",
      );
    }

    return this.enrichSite(response.data);
  }

  /**
   * Fetch sites assigned to a study via study-site-assignments API.
   * Backend: GET /study-site-assignments/by-study/:studyId (returns assignments with site relation).
   */
  async getAllSites(filters?: SiteFilters): Promise<Site[]> {
    const allSites: Site[] = [];
    let currentPage = 1;
    const limit = 100;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await this.getSites(filters, currentPage, limit);

      if (response.sites && response.sites.length > 0) {
        allSites.push(...response.sites);

        const totalPages = Math.ceil(response.pagination.totalSites / limit);
        hasMorePages = currentPage < totalPages;
        currentPage++;
      } else {
        hasMorePages = false;
      }
    }

    return allSites;
  }

  async getSitesByStudy(studyId: string): Promise<Site[]> {
    // Raw shape from /study-site-assignments/by-study/:id
    interface RawApiSite {
      id: string;
      name: string;
      code?: string | null;
      address?: { city?: string; street?: string; country?: string };
      city?: string;
      country?: string;
      phone?: string | null;
      email?: string | null;
      status?: string;
      isActive?: boolean;
      principalInvestigators?: {
        id: string;
        name: string;
        email?: string | null;
        studyName?: string;
      }[];
    }
    interface AssignmentWithSite {
      siteId?: string;
      site?: RawApiSite;
      assignedPis?: {
        principalInvestigatorId: string;
        principalInvestigator?: {
          id: string;
          name: string;
          email?: string | null;
        };
      }[];
    }

    const normalizeSite = (
      raw: RawApiSite,
      assignment?: AssignmentWithSite,
    ): Site => {
      const assignmentPis = (assignment?.assignedPis ?? [])
        .map((link) => link.principalInvestigator)
        .filter(
          (pi): pi is { id: string; name: string; email?: string | null } =>
            Boolean(pi?.id && pi.name),
        )
        .map((pi) => ({
          id: pi.id,
          name: pi.name,
          email: pi.email ?? "",
        }));

      const rawPis = (raw.principalInvestigators ?? []).map((pi) => ({
        id: pi.id,
        name: pi.name,
        email: pi.email ?? "",
        studyName: pi.studyName,
      }));

      const principalInvestigators = Array.from(
        new Map(
          [...assignmentPis, ...rawPis].map((pi) => [pi.id, pi]),
        ).values(),
      );

      return {
        id: raw.id,
        siteNumber: raw.code ?? undefined,
        name: raw.name,
        location: {
          city: raw.address?.city ?? raw.city ?? "",
          country: raw.address?.country ?? raw.country ?? "",
          address: raw.address?.street ?? "",
        },
        assignedStudies: [studyId],
        studyAssignments: [],
        principalInvestigators,
        studyCoordinators: [],
        status: raw.isActive === false ? "Deactivated" : "Active",
        contactDetails: {
          phone: raw.phone ?? undefined,
          email: raw.email ?? undefined,
        },
        createdDate: "",
        lastModified: "",
      };
    };

    const response: ApiResponse<AssignmentWithSite[]> =
      await httpCoreService.get<AssignmentWithSite[]>(
        `/study-site-assignments/by-study/${studyId}`,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch sites",
      );
    }

    const assignments = Array.isArray(response.data) ? response.data : [];
    const sites: Site[] = [];
    const seenIds = new Set<string>();

    for (const a of assignments) {
      if (a.site?.id) {
        const sid = a.site.id;
        if (!seenIds.has(sid)) {
          seenIds.add(sid);
          sites.push(normalizeSite(a.site, a));
        }
      } else if (typeof a.siteId === "string" && a.siteId) {
        if (!seenIds.has(a.siteId)) {
          seenIds.add(a.siteId);
          try {
            const site = await this.getSiteById(a.siteId);
            sites.push(site);
          } catch {
            // skip if site fetch fails
          }
        }
      }
    }

    return sites;
  }

  async getSiteById(id: string): Promise<Site> {
    const response: ApiResponse<Site> = await httpCoreService.get<Site>(
      `/sites/${id}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch site",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    const data = response.data;
    return this.enrichSite(data);
  }

  async updateSite(id: string, updates: UpdateSiteRequest): Promise<Site> {
    const response: ApiResponse<Site> = await httpCoreService.patch<
      Site,
      UpdateSiteRequest
    >(`/sites/${id}`, updates);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to update site",
      );
    }

    if (!response.data) {
      throw new Error(
        "Site update succeeded but no data was returned from the server.",
      );
    }

    return response.data;
  }

  async deleteSite(id: string): Promise<void> {
    const response: ApiResponse<null> = await httpCoreService.delete<null>(
      `/sites/${id}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to delete site",
      );
    }
  }

  async getSites(
    filters?: SiteFilters,
    page = 1,
    limit = 10,
  ): Promise<SiteListResponse> {
    // Get tenant ID from logged-in user
    const user = getStoredUser();
    if (!user?.tenantId) {
      throw new Error("User not authenticated or tenant ID not found");
    }

    const queryParams = new URLSearchParams({
      tenant_id: user.tenantId,
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add optional filters to query params (excluding tenant_id as it's automatically set)
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (
          key !== "tenant_id" &&
          value !== undefined &&
          value !== null &&
          value !== ""
        ) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response: ApiResponse<unknown> = await httpCoreService.get<unknown>(
      `/sites?${queryParams.toString()}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch sites",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    const payload = response.data;

    const isPaginatedPayload = isPaginatedSitesPayload(payload);

    const items = isPaginatedPayload ? payload.items : payload;
    const totalSites = isPaginatedPayload
      ? payload.total
      : Array.isArray(items)
        ? items.length
        : 0;
    const responsePage = isPaginatedPayload ? (payload.page ?? page) : page;
    const responseLimit = isPaginatedPayload ? (payload.limit ?? limit) : limit;
    const totalPages = isPaginatedPayload
      ? (payload.totalPages ?? Math.ceil(totalSites / responseLimit))
      : Math.ceil(totalSites / responseLimit);

    const siteListResponse: SiteListResponse = {
      sites: (Array.isArray(items) ? items : []).map((s) =>
        this.enrichSite(s as RawSiteData),
      ),
      pagination: {
        currentPage: responsePage,
        pageSize: responseLimit,
        totalSites,
        totalPages,
      },
    };

    return siteListResponse;
  }

  async activateSite(id: string): Promise<Site> {
    const response: ApiResponse<Site> = await httpCoreService.post<Site>(
      `/sites/${id}/activate`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to activate site",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    return response.data;
  }

  async archiveSite(id: string): Promise<Site> {
    const response: ApiResponse<Site> = await httpCoreService.post<Site>(
      `/sites/${id}/archive`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to archive site",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    return response.data;
  }
}

// Singleton instance
export const siteRepository = new SiteRepository();
